import { isMirrorModeElement } from 'js/utils/DOM.util';

import * as Types from './types';

export const pxToPercents = (container: HTMLDivElement, widthPx: number): number => {
    if (!container || !widthPx) {
        return 0;
    }

    return widthPx / container.clientWidth * 100;
};

export const percentsToPx = (container: HTMLDivElement, widthPercents: number): number => {
    if (!container || !widthPercents) {
        return 0;
    }

    return widthPercents * container.clientWidth / 100;
};

export const getSlideIndex = (index: number, slidesCount: number): number => {
    let slideIndex = index;

    if (index < 0) {
        slideIndex = slidesCount + index;
    } else if (slideIndex >= slidesCount) {
        slideIndex = slideIndex - slidesCount;
    }

    return slideIndex;
};

export const getVirtualIndexes = ({
    loop, activeSlide, slidesCount, itemsPerView
}: {
    loop: boolean; activeSlide: number; slidesCount: number; itemsPerView: number;
}): { from: number; to: number } => {
    const slidesBefore = itemsPerView + 1;
    const from = activeSlide - slidesBefore;
    const to = activeSlide + 2 * itemsPerView + 1;

    if (!loop) {
        return {
            from: Math.max(from, 0),
            to: Math.min(to, slidesCount - 1)
        };
    }

    return { from, to };
};

export const getCurrentTranslateX = (container: HTMLDivElement): number => {
    const style = window.getComputedStyle(container);
    const matrix = new WebKitCSSMatrix(style.transform);

    return pxToPercents(container, matrix.m41);
};

export const getActualSlidesDiff = ({
    activeSlide, prevSlide, direction, slidesCount
}: Types.IGetActualSlidesDiff): number => {
    const diff = activeSlide - prevSlide;

    if (direction === 'right') {
        return diff < 0 ? slidesCount - Math.abs(diff) : diff;
    }

    return diff > 0 ? slidesCount - diff : Math.abs(diff);
};

export const getContainerShift = ({
    container, loop, activeSlide, itemWidth,
    itemsPerView, slidesCount, gap, tailWidth,
    prevSlide, direction, storeContainerShift
}: Types.IGetContainerShift): number => {
    const gapPercents = pxToPercents(container, gap);
    const itemWidthPercents = pxToPercents(container, itemWidth);
    const width = itemWidthPercents + gapPercents;
    const isMirrorMode = isMirrorModeElement(container);
    const multiplier = isMirrorMode ? 1 : -1;
    const currentShift = getCurrentTranslateX(container);

    if (!loop) {
        if (activeSlide >= slidesCount - itemsPerView) {
            return multiplier * (activeSlide * width - (tailWidth ? tailWidth + gapPercents : 0));
        }

        return multiplier * activeSlide * width;
    }

    const diff = getActualSlidesDiff({ activeSlide, prevSlide, direction, slidesCount });
    const max = Math.max(storeContainerShift + diff * width, currentShift);
    const min = Math.min(storeContainerShift - diff * width, currentShift);

    if (isMirrorMode) {
        return direction === 'right' ? max : min;
    }

    return direction === 'right' ? min : max;
};

export const getItemShift = ({
    container, containerShift, loop, slidesCount,
    activeSlide, itemWidth, itemsPerView, gap
}: Types.IGetItemShift): number => {
    const slidesBefore = itemsPerView + 1;
    const itemWidthPercents = pxToPercents(container, itemWidth);
    const gapPercents = pxToPercents(container, gap);
    const width = itemWidthPercents + gapPercents;
    const multiplier = isMirrorModeElement(container) ? 1 : -1;

    // only one view
    if (slidesCount <= itemsPerView) {
        return 0;
    }

    if (!loop) {
        if (activeSlide < slidesBefore) {
            return 0;
        }

        return -multiplier * (activeSlide - slidesBefore) * width;
    }

    return multiplier * slidesBefore * width - containerShift;
};

export const getItemsWithModifiedKeys = ({
    activeSlide, direction, nextItems,
    prevItems, prevSlide, slidesCount
}: Types.IGetItemsWithModifiedKeys): Types.IVisualItem[] => {
    const diff = getActualSlidesDiff({ activeSlide, prevSlide, direction, slidesCount });
    const prevStart = prevItems[0].virtualKey;
    const start = direction === 'right' ? prevStart + diff : prevStart - diff;

    return nextItems.map((item, index) => ({
        ...item,
        virtualKey: start + index
    }));
};
